import { gql } from "@apollo/client";
import { AccountFragment } from "fragments/account";

// No `rows`. A table's rows are a field on the server precisely so that a
// listing does not pay for an S3 read and a multi-megabyte response per row —
// so the fragment every screen shares must not select them. `getWithRows` below
// is the one query that does, and it is the edit screen's alone.
export const TableFragment = gql`
  fragment TableFragment on Table {
    id
    tableId
    title
    description
    columns
    tags
    status
    rowCount
    rowsEtag

    sourceType
    source
    credentialHint
    # Selected by the FRAGMENT, so the listing carries it too — a table whose
    # sync failed should say so on the list, not only once you open it.
    sync

    createdAt
    updatedAt
  }
`;

export const get = gql`
  query TableGet($tableId: String!) {
    viewer {
      id
      account {
        ...AccountFragment
        tables {
          id
          get(tableId: $tableId) {
            ...TableFragment
          }
        }
      }
    }
  }
  ${AccountFragment}
  ${TableFragment}
`;

// The rows, and the etag they were read at.
//
// `fetchPolicy: "network-only"` at the call site rather than here: the etag is a
// concurrency token, and answering a row editor from the cache would hand it a
// version that may already have been replaced — which is a conflict on save
// that the person cannot explain.
export const getWithRows = gql`
  query TableGetWithRows($tableId: String!) {
    viewer {
      id
      account {
        ...AccountFragment
        tables {
          id
          get(tableId: $tableId) {
            ...TableFragment
            rows {
              id
              etag
              count
              columns
              entries
            }
          }
        }
      }
    }
  }
  ${AccountFragment}
  ${TableFragment}
`;

export const list = gql`
  query TableList($input: ListInput) {
    viewer {
      id
      account {
        id
        tables {
          id
          list(input: $input) {
            id
            last
            items {
              ...TableFragment
            }
          }
        }
      }
    }
  }
  ${TableFragment}
`;

export const search = gql`
  query TableSearch($input: SearchInput!) {
    viewer {
      id
      account {
        id
        tables {
          id
          search(input: $input) {
            id
            last
            items {
              ...TableFragment
            }
          }
        }
      }
    }
  }
  ${TableFragment}
`;

export const create = gql`
  mutation TableNew($input: TableInputCreate!) {
    viewer {
      account {
        tables {
          create(input: $input) {
            ...TableFragment
          }
        }
      }
    }
  }
  ${TableFragment}
`;

export const edit = gql`
  mutation TableEdit($tableId: String!, $input: TableInputUpdate!) {
    viewer {
      account {
        tables {
          get(tableId: $tableId) {
            update(input: $input) {
              ...TableFragment
            }
          }
        }
      }
    }
  }
  ${TableFragment}
`;

// The rows write, which returns the TABLE rather than the rows: what the caller
// needs back is the new `rowsEtag` to condition its next write on, and re-reading
// the entries it just sent would double the payload for nothing.
export const writeRows = gql`
  mutation TableWriteRows($tableId: String!, $input: TableRowsInputWrite!) {
    viewer {
      account {
        tables {
          get(tableId: $tableId) {
            writeRows(input: $input) {
              ...TableFragment
            }
          }
        }
      }
    }
  }
  ${TableFragment}
`;

// Start a sync. Returns immediately with sync.status = "processing"; the screen
// polls `get` until it moves. Nothing here waits for the pull.
export const sync = gql`
  mutation TableSync($tableId: String!) {
    viewer {
      account {
        tables {
          get(tableId: $tableId) {
            sync {
              ...TableFragment
            }
          }
        }
      }
    }
  }
  ${TableFragment}
`;

// One way. The secret goes up and the TABLE comes back — there is no query that
// returns a credential, only `credentialHint`. An empty `secret` clears it.
export const setCredential = gql`
  mutation TableSetCredential($tableId: String!, $input: TableCredentialInput!) {
    viewer {
      account {
        tables {
          get(tableId: $tableId) {
            setCredential(input: $input) {
              ...TableFragment
            }
          }
        }
      }
    }
  }
  ${TableFragment}
`;

export const remove = gql`
  mutation TableRemove($tableId: String!) {
    viewer {
      account {
        tables {
          get(tableId: $tableId) {
            remove
          }
        }
      }
    }
  }
`;

// Evict every cached entry under this service's table namespace.
//
// The literal `tables/queries` is what TableQueries.id resolves to on the
// server, and the two are pinned against each other in
// api/code/test/schema.test.js — if they drift, a create succeeds and the list
// screen stays stale with no error anywhere.
export const update = ({ tableId } = {}) => (cache) => {
  Object.keys(cache.data.data).forEach((key) => {
    if (key.includes(`tables/queries`)) {
      cache.data.delete(key);
    }
  });
};
